import React, { useEffect } from 'react';
import { styled, Checkbox as MuiCheckbox } from '@mui/material';
import clsx from 'clsx';
import CheckboxGroupContext from './context';
import { IconCheck, IconDash } from './IconComponents';
import { checkboxClasses, getStyle, getIconStyle, getCheckedIconStyle, } from './style';
import { useMixedValue } from '../../hooks/useMixedValue';
import { extractAriaProps } from '../../Shared/props';
import { useUniqueId } from '../../hooks/useUniqueId';
const StyledIcon = styled('span')((props) => getIconStyle(props.theme));
const StyledCheckedIcon = styled(StyledIcon)((props) => getCheckedIconStyle(props.theme));
const CheckboxBase = React.forwardRef((props, ref) => {
    const groupContext = React.useContext(CheckboxGroupContext);
    const { className, children, checkedIcon, defaultChecked, indeterminate, onChange, indeterminateIcon, icon, value, id, sx, description, inputProps, ...restProps } = props;
    const [ariaProps, rest] = extractAriaProps(restProps);
    const [inputAriaProps, inputRestProps] = extractAriaProps(inputProps ?? {});
    const getId = useUniqueId('checkbox');
    const ariaId = getId('label');
    const ariaDescId = getId('desc');
    const internalArias = {};
    if (children) {
        Object.assign(internalArias, { 'aria-labelledby': ariaId });
    }
    if (description) {
        Object.assign(internalArias, { 'aria-describedby': ariaDescId });
    }
    let { disabled: checkboxDisabled, checked: checkboxChecked, name: checkboxName, onChange: onCheckboxChange, } = props;
    const inputRef = React.useRef(null);
    if (groupContext) {
        const onInnerCheckboxChange = (e, checked) => {
            onChange?.(e, checked);
            groupContext?.onChange(value);
        };
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        checkboxDisabled = checkboxDisabled || groupContext.disabled;
        checkboxChecked = !!groupContext.value?.includes(value);
        checkboxName = groupContext.name;
        onCheckboxChange = onInnerCheckboxChange;
    }
    const [checkedState, setCheckedState] = useMixedValue(checkboxChecked, defaultChecked);
    function handleChange(event, checked) {
        setCheckedState(checked);
        onCheckboxChange?.(event, checked);
    }
    useEffect(() => {
        if (inputRef.current) {
            // To fix AX issue for half-checked checkbox on Safari/Chrome+VoiceOver
            inputRef.current.indeterminate = !!indeterminate;
        }
    }, [indeterminate]);
    return (React.createElement("div", { className: clsx(checkboxClasses.root, className) },
        React.createElement("label", { className: checkboxClasses.title, htmlFor: id },
            React.createElement(MuiCheckbox, { id: id, ref: ref, name: checkboxName, disableRipple: true, checked: !!checkedState, disabled: checkboxDisabled, indeterminate: !!indeterminate, onChange: handleChange, value: value, icon: icon ?? React.createElement(StyledIcon, { "aria-hidden": true }), checkedIcon: checkedIcon ?? (React.createElement(StyledCheckedIcon, { "aria-hidden": true },
                    React.createElement(IconCheck, null))), indeterminateIcon: indeterminateIcon ?? (React.createElement(StyledCheckedIcon, { "aria-hidden": true },
                    React.createElement(IconDash, null))), className: clsx(checkboxClasses.base), inputProps: {
                    'aria-checked': indeterminate ? 'mixed' : !!checkedState,
                    'aria-disabled': checkboxDisabled,
                    ...internalArias,
                    ...inputAriaProps,
                    ...ariaProps,
                    ...inputRestProps,
                }, ...rest, inputRef: inputRef, sx: sx }),
            Boolean(children) && (React.createElement("span", { id: ariaId, className: checkboxClasses.label, "aria-hidden": true }, children))),
        Boolean(description) && (React.createElement("span", { id: ariaDescId, className: checkboxClasses.description }, description))));
});
const StyledCheckbox = styled(CheckboxBase)(({ theme, disabled }) => getStyle(theme, { disabled }));
const Checkbox = React.forwardRef((props, ref) => (React.createElement(StyledCheckbox, { ...props, ref: ref })));
export default Checkbox;
